(function () {
  // Detect touch-primary devices (where CSS hover:none applies)
  const isTouchDevice = () => window.matchMedia('(hover: none)').matches;

  class HeritageCardGallery {
    constructor(node) {
      if (!node || node.dataset.galleryInitialized === 'true') {
        return;
      }
      this.node = node;
      this.slides = Array.from(node.querySelectorAll('[data-gallery-item]'));
      this.prevButton = node.querySelector('[data-gallery-prev]');
      this.nextButton = node.querySelector('[data-gallery-next]');
      this.dots = Array.from(node.querySelectorAll('[data-gallery-dot]'));
      this.track = node.querySelector('[data-gallery-track]');
      this.viewport = node.querySelector('[data-gallery-viewport]');
      this.index = 0;
      this._scrollSyncTimer = null;

      if (this.slides.length <= 1 || !this.track) {
        node.dataset.galleryInitialized = 'true';
        node.dataset.galleryReady = 'true';
        node.dataset.hasPrev = 'false';
        node.dataset.hasNext = 'false';
        node.dataset.galleryIndex = '0';
        return;
      }

      this.bindEvents();
      this.update();
      node.dataset.galleryInitialized = 'true';
      node.dataset.galleryReady = 'true';
      this._initAutoAdvance();
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.showPrevious();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.showNext();
        });
      }

      const handleKey = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPrevious();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNext();
        }
      };

      [this.node, this.prevButton, this.nextButton].forEach((target) => {
        if (target) {
          target.addEventListener('keydown', handleKey);
        }
      });

      // On touch devices, native scroll-snap handles the swipe.
      // We only need to listen to scroll events to keep dots/index in sync.
      if (this.viewport) {
        this.viewport.addEventListener('scroll', () => {
          clearTimeout(this._scrollSyncTimer);
          this._scrollSyncTimer = setTimeout(() => {
            this._syncIndexFromScroll();
          }, 80);
        }, { passive: true });
      }
    }

    _syncIndexFromScroll() {
      if (!this.viewport) return;
      const slideWidth = this.viewport.offsetWidth;
      if (slideWidth === 0) return;
      const newIndex = Math.round(this.viewport.scrollLeft / slideWidth);
      const clamped = Math.max(0, Math.min(this.slides.length - 1, newIndex));
      if (clamped === this.index) return;
      this.index = clamped;
      this._updateStateOnly();
    }

    showPrevious() {
      if (this.index === 0) {
        return;
      }
      this.goTo(this.index - 1);
    }

    showNext() {
      if (this.index >= this.slides.length - 1) {
        return;
      }
      this.goTo(this.index + 1);
    }

    goTo(index) {
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      if (clamped === this.index) {
        return;
      }
      this.index = clamped;
      this.update();
    }

    update() {
      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.updateTrackPosition();

      if (this.prevButton) {
        this.prevButton.disabled = this.index === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.index === this.slides.length - 1;
      }

      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === this.index);
      });

      this.updateDatasetFlags();
      this.node.dataset.galleryIndex = this.index;
    }

    // Update dots/flags/buttons only — no position change (used after native scroll sync)
    _updateStateOnly() {
      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (this.prevButton) {
        this.prevButton.disabled = this.index === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.index === this.slides.length - 1;
      }

      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === this.index);
      });

      this.updateDatasetFlags();
      this.node.dataset.galleryIndex = this.index;
    }

    updateTrackPosition() {
      if (isTouchDevice()) {
        // Mobile: scroll the viewport natively (CSS scroll-snap takes care of snapping)
        if (this.viewport) {
          const targetScroll = this.index * this.viewport.offsetWidth;
          this.viewport.scrollTo({ left: targetScroll, behavior: 'smooth' });
        }
      } else {
        // Desktop: transform-based slide
        if (!this.track) return;
        const offset = this.index * -100;
        this.track.style.transform = `translateX(${offset}%)`;
      }
    }

    updateDatasetFlags() {
      this.node.dataset.hasPrev = this.index > 0 ? 'true' : 'false';
      this.node.dataset.hasNext = this.index < this.slides.length - 1 ? 'true' : 'false';
    }

    // Auto-advance on desktop hover — cycles through images every 2s (UX #2)
    _initAutoAdvance() {
      if (isTouchDevice()) return;
      this._autoTimer = null;
      const cardWrapper = this.node.closest('.card-wrapper') || this.node;
      cardWrapper.addEventListener('mouseenter', () => {
        this._autoTimer = setInterval(() => {
          // Loop back to start when at last slide
          if (this.index >= this.slides.length - 1) {
            this.goTo(0);
          } else {
            this.showNext();
          }
        }, 2000);
      });
      cardWrapper.addEventListener('mouseleave', () => {
        clearInterval(this._autoTimer);
        this._autoTimer = null;
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-card-gallery]').forEach((node) => {
      if (node.dataset.galleryInitialized === 'true') {
        return;
      }
      new HeritageCardGallery(node);
    });
  }

  window.HeritageCardGallery = window.HeritageCardGallery || {};
  window.HeritageCardGallery.initAll = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();